import { createClient, type SupabaseClient } from '@supabase/supabase-js';

import { recordHeartbeat } from '@/lib/realtime-health';

// NOTE: These must be accessed as static `process.env.NEXT_PUBLIC_*` member
// expressions (not via readEnv's dynamic `process.env[name]` lookup) so that
// Next.js inlines them into the client bundle. A computed lookup is never
// substituted, so every one of these resolved to `undefined` in the browser and
// every realtime client factory returned null on every call , which silently
// disabled ALL realtime in the app (floor, KDS, guest order tracking, delivery).
// Same pattern and same reason as lib/supabase.ts and lib/storageClient.ts.
const realtimeUrl = process.env.NEXT_PUBLIC_SUPABASE_DB_URL;
const realtimeAnonKey = process.env.NEXT_PUBLIC_SUPABASE_DB_ANON_KEY;

const ANON_TOKEN_KEY = '\u0000realtime-anon';

let sharedScopedClient: SupabaseClient | null = null;
let sharedScopedToken: string | null = null;
const guestScopedClients = new Map<
  string,
  { client: SupabaseClient; token: string | null; refs: number }
>();

/**
 * One SupabaseClient instance maps to one WebSocket connection; every channel
 * created on that instance multiplexes over it. Sharing a client across hooks
 * therefore collapses many realtime connections into one per browser tab,
 * which is what keeps us under the Supabase realtime concurrent-peak billing
 * limit (previously each `useRealtime*` hook opened its own scoped client and
 * its own socket: a public menu page alone held six, one per table it watched).
 *
 * These clients do NOT persist sessions and exist only to hold realtime
 * channels with RLS-correct auth claims without clobbering the shared
 * session-bearing client in lib/supabase.ts.
 *
 * IMPORTANT: the `accessToken` client option must NOT be wired in here. In
 * supabase-js 2.86.0 the RealtimeClient always binds the callback into its
 * heartbeat path: after a channel joins (and on every heartbeat) it re-runs
 * the callback and pushes whatever it returns as an `access_token` event, so a
 * callback that closes over one token silently overwrites any newer claims a
 * later `setAuth()` established. Instead the factory creates a callback-free
 * client and callers establish claims with an explicit, pre-subscribe
 * `client.realtime.setAuth(token)`; without a callback that call sticks.
 */
function createRealtimeClient(): SupabaseClient | null {
  if (!realtimeUrl || !realtimeAnonKey) return null;

  return createClient(realtimeUrl, realtimeAnonKey, {
    auth: {
      persistSession: false,
      // This client points at the same project as the shared auth client, so
      // without an explicit key every scoped client would register another
      // GoTrueClient under `sb-<ref>-auth-token` -- the browser console showed
      // seven on the public menu alone. It holds no session (persistSession is
      // false and claims arrive via setAuth), so give it its own key and no
      // refresh timer instead of sharing the real one.
      storageKey: 'sb-menuraq-realtime-scoped',
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    realtime: {
      // OFF-03: feed every heartbeat into the central liveness monitor so a
      // dropped channel is detected even when no table changes arrive (R27.8).
      heartbeatCallback: (status) => recordHeartbeat(status),
    },
  });
}

/**
 * Returns the single per-tab realtime client for session-scoped hooks
 * (`useRealtimeRefresh`, `useRealtimeInvalidate`). All of their channels
 * multiplex over this one connection regardless of how many are mounted
 * (AuthContext profiles + business + menu tables + floor refresh all share a
 * single socket instead of opening one each). It is intentionally long-lived:
 * it represents the tab's realtime connection and is never torn down while the
 * tab is open, so an idle socket with zero channels is the expected steady
 * state rather than a leak.
 *
 * When the current session token changes (sign in/out, token refresh that
 * alters claims), the next acquire with the new token re-auths the client via
 * `setAuth`, which in supabase-js 2.86.0 updates the join payload and pushes
 * the new claims to every channel on the connection.
 *
 * Returns null if the required env vars are missing (e.g. in environments
 * where realtime is not needed).
 */
export function getSharedScopedSupabaseClient(accessToken?: string | null): SupabaseClient | null {
  if (!realtimeUrl || !realtimeAnonKey) return null;

  const token = accessToken ?? null;
  if (!sharedScopedClient) {
    sharedScopedClient = createRealtimeClient();
    if (!sharedScopedClient) return null;
    sharedScopedToken = token;
    if (token) void sharedScopedClient.realtime.setAuth(token);
    return sharedScopedClient;
  }

  if (token !== sharedScopedToken) {
    sharedScopedToken = token;
    if (token) void sharedScopedClient.realtime.setAuth(token);
  }
  return sharedScopedClient;
}

/**
 * Returns a realtime client scoped to `accessToken` (typically a guest order
 * JWT), creating one per distinct token and sharing it across every hook that
 * carries the same token. This is what lets a status hook and a delivery hook
 * on the same order page share one connection. Clients are reference-counted:
 * callers must pair each `acquireScopedSupabaseClient(accessToken)` with a
 * `releaseScopedSupabaseClient(accessToken)`, and the connection is closed
 * once the last holder releases it so guest-token sockets never pile up while
 * a visitor browses from one order to the next.
 *
 * Distinct tokens still need distinct connections (RLS is evaluated per
 * connection), but the common case of a page holding one order token collapses
 * to a single socket.
 *
 * Returns null if the required env vars are missing.
 */
export function acquireScopedSupabaseClient(accessToken?: string | null): SupabaseClient | null {
  if (!realtimeUrl || !realtimeAnonKey) return null;

  const token = accessToken ?? null;
  const key = token ?? ANON_TOKEN_KEY;
  const existing = guestScopedClients.get(key);
  if (existing) {
    existing.refs += 1;
    if (token && token !== existing.token) {
      existing.token = token;
      void existing.client.realtime.setAuth(token);
    }
    return existing.client;
  }

  const client = createRealtimeClient();
  if (!client) return null;
  if (token) void client.realtime.setAuth(token);
  guestScopedClients.set(key, { client, token, refs: 1 });
  return client;
}

/**
 * Releases a reference previously taken by `acquireScopedSupabaseClient`.
 * The underlying connection is closed when the last reference is released.
 */
export function releaseScopedSupabaseClient(accessToken?: string | null): void {
  if (!realtimeUrl || !realtimeAnonKey) return;

  const key = accessToken ?? ANON_TOKEN_KEY;
  const entry = guestScopedClients.get(key);
  if (!entry) return;

  entry.refs -= 1;
  if (entry.refs <= 0) {
    guestScopedClients.delete(key);
    try {
      entry.client.realtime.disconnect();
    } catch {
      // Disconnect on a never-connected socket is a no-op.
    }
  }
}

export type PostgresChangesEvent = '*' | 'INSERT' | 'UPDATE' | 'DELETE';

export type RealtimeSubscriptionConfig = {
  table: string;
  event?: PostgresChangesEvent;
  filter?: string;
  schema?: string;
};

/**
 * Sanitizes a channel name for Supabase realtime.
 */
export function sanitizeChannelName(value: string): string {
  return value.replace(/[^a-zA-Z0-9:_-]/g, '_').slice(0, 96);
}
